import React, { useContext, useEffect, useState, useCallback, useMemo } from "react";
import { Navigate, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import Context from "../../context";
import Home from "../Home";
import Summary from "../Summary";
import Brief from "../Brief";
import DailySummary from "../Brief/Summary";
import DailyFlights from "../Brief/Flights";
import DailyDocumentViewer from "../Brief/DocumentViewer";
import DailyPosts from "../Brief/Posts";
import DailyMap from "../Brief/Map";
import DailyWeather from "../Brief/Weather";
import Documents from "../Documents";
import PoiList from "../PoiList";
import Map from "../Map";
import Posts from "../Posts";
import Weather from "../Weather";
import Flights from "../Flights";
import Inspirations from "../Inspirations";
import Directory from '../Directory';
import HotelInfo from '../HotelInfo';
import LoadingScreen from "../global/LoadingScreen";
import NotFoundScreen from "../global/errorPages/NotFoundScreen";
import FaqTermsAbout from "../FaqTermsAbout";
import ViewAll from "../ViewAll";
import { db } from "../../db";
import Settings from "../Settings";
import { EwaContext } from "../../context/EwaContext";
import DocumentViewer from "../global/DocumentViewer";

const Itinerary = () => {
  const {
    itin,
    icons,
    t,
    navMenuItems,
    setNavMenuItems,
    setNavActiveIdx,
    key,
    viewMode,
  } = useContext(Context);
  const { setEwaDocs } = useContext(EwaContext);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const hamburgerMenuNavigationItems = useMemo(() => [
    { path: 'settings', component: <Settings /> },
    { path: 'faq', component: <FaqTermsAbout type="faq" /> },
    { path: 'about', component: <FaqTermsAbout type="about" /> },
    { path: 'terms', component: <FaqTermsAbout type="terms" /> },
    { path: 'view-all', component: <ViewAll /> },
  ], []);

  const [shouldDisplayBookNow, setShouldDisplayBookNow] = useState(true);
  const [menuItemOptions] = useState({
    summary: { path: '/summary', enabled: true, dataField: 'brief' },
    daily: { path: '/daily', subPath: '/1', enabled: true, dataField: 'brief', forceValidNavItem: itin?.menu?.find(x => x.segue === 'summary') },
    documents: { path: '/documents', enabled: true, dataField: 'documents' },
    messaging: { path: null, enabled: false },
    posts: { path: '/posts', subPath: '/1', enabled: true, isOffField: 'hideJournal' },
    maps: { path: '/maps', enabled: true, dataField: 'locations', dataFieldValidationField: 'showInMaps' },
    poiList: { path: '/pois', enabled: true, dataField: 'pois' },
    weather: { path: '/weather', enabled: true, dataField: 'locations', dataFieldValidationField: 'showInWeather' },
    flights: { path: '/flights', enabled: true, dataField: 'flights' },
    inspirations: { path: '/inspirations', subPath: '/1/1', enabled: true, dataField: 'overlay' },

    // Stays
    gallery: { path: '/gallery', enabled: true, dataField: 'brief' },
    directory: { path: '/directory', enabled: true, dataField: 'directories' },
    dailyActivities: { path: '/daily-activities', subPath: '/1', enabled: true, dataField: 'dailyActivities' },
    vouchers: { path: '/vouchers', enabled: true, dataField: 'vouchers' },
    hotelInfo: { path: '/hotel-info', enabled: true, },
    doNotDisturb: { path: null, enabled: false }
  });

  const urlKeyPath = `/${itin.localData.urlKey}`;
  const buildInitialPath = useCallback((menuItemOption, segueId) => (segueId
    ? `${urlKeyPath}${menuItemOption.path}/${itin.documents.find(doc => doc.id === segueId)?.pathName}`
    : `${urlKeyPath}${menuItemOption.path}`), [itin.documents, urlKeyPath]);
  const buildFullPath = useCallback((menuItemOption, segueId) => {
    let fullPath = `${buildInitialPath(menuItemOption, segueId)}${menuItemOption.subPath ?? ''}`;
    if (segueId) {
      const doc = itin.documents.find(doc => doc.id === segueId);
      if (doc?.displayMode === 'gallery' && !!doc?.autoOpenId) {
        const data = doc.entries.map(id => itin.documents.find(itinDoc => itinDoc.id === id));
        const slideNum = data.findIndex(x => x.id === doc.autoOpenId) + 1 || 1;
        fullPath += `/gallery/${slideNum}`;
      }
    }
    return fullPath;
  }, [buildInitialPath, itin.documents]);

  useEffect(() => {
    const nestedId = key?.split('-')[1];
    if (!nestedId) return;

    let isMounted = true;

    const getDisplayBookNow = async () => {
      const currentNestedStay = await db.itineraries
        .where({ vamoosId: parseInt(nestedId) })
        .toArray();
      if (isMounted) {
        setShouldDisplayBookNow(currentNestedStay[0]?.displayBookNow);
      }
    }

    getDisplayBookNow();
    return () => {
      isMounted = false;
    };
  }, [key]);

  const updateNavMenuItems = useCallback(() => {
    if (!itin) return;
    if (itin.type === 'inspiration') {
      const inspirationMenuItemOptions = {
        title: itin.inspirationsLabel,
        initialPath: buildInitialPath(menuItemOptions.inspirations, null),
        path: buildFullPath(menuItemOptions.inspirations, null),
      };
      setNavMenuItems([inspirationMenuItemOptions]);
      if (!pathname.startsWith(inspirationMenuItemOptions.initialPath) && !hamburgerMenuNavigationItems.some(item => pathname.includes(item.path))) navigate(inspirationMenuItemOptions.path);
      return;
    }

    const foundInMenuItemOptions = Object.values(menuItemOptions).find(x => pathname.startsWith(buildInitialPath(x, null)));
    let isValidNavItem = false;
    const navItems = [ { title: t('home', { lng: itin.language }), path: urlKeyPath + '/', icon: <img className="nav-item-icon" src="/icons/home_icon.svg" alt="" /> }];
    for (const { label, segue, iconId, segueId } of itin.menu) {
      if (!menuItemOptions[segue]) {
        console.error('Menu Item Not Found: ' + segue)
        continue
      }
      if (!menuItemOptions[segue].enabled) continue;

      // Ensure itin contains structures where necessary
      const { dataField, dataFieldValidationField, isOffField, isOnField } = menuItemOptions[segue];
      if (dataField) {
        if (!itin[dataField]) continue;
        if (Array.isArray(itin[dataField]) && !itin[dataField].length) continue;
        if (dataFieldValidationField && itin[dataField].every(x => !x[dataFieldValidationField])) continue;
      }
      if ((isOffField && itin[isOffField]) || (isOnField && !itin[isOnField])) continue;

      const icon = <img className="nav-item-icon" alt="" src={icons[iconId]} />
      const item = {
        title: label,
        initialPath: buildInitialPath(menuItemOptions[segue], segue === 'documents' ? segueId : null),
        path: buildFullPath(menuItemOptions[segue], segue === 'documents' ? segueId : null),
        icon,
      };

      if (segue === 'documents') {
        const doc = itin[dataField].find(doc => doc.id === segueId);
        if (doc?.type === 'file' || doc?.type === 'bundle') item.path = `${item.initialPath}/view/${doc.id}`
        if (doc?.type === 'remote') {
          item.type = 'external';
          item.path = doc?.remoteUrl;
        }
      }

      // to hide action buttons for the nested stays
      if (segue === 'documents' && !shouldDisplayBookNow) continue;

      if (item.type === 'external' || pathname.startsWith(item.initialPath)) isValidNavItem = true;
      navItems.push(item);
    }
    if (foundInMenuItemOptions?.forceValidNavItem) isValidNavItem = true;

    if (!pathname.match(new RegExp(`^${urlKeyPath}[/]?$`)) && foundInMenuItemOptions && !isValidNavItem) {
      navigate(urlKeyPath);
    }
    setNavMenuItems(navItems);
    // adding navigate and pathname to the current array of dependencies will cause this callback to run redundantly
    // eslint-disable-next-line
  }, [hamburgerMenuNavigationItems, setNavMenuItems, menuItemOptions, icons, itin, t, buildInitialPath, buildFullPath, urlKeyPath, shouldDisplayBookNow]);

  useEffect(() => {
    updateNavMenuItems();
  }, [urlKeyPath, itin.id, shouldDisplayBookNow, viewMode, updateNavMenuItems]);

  useEffect(() => {
    if (!navMenuItems) return;
    if (!pathname) return;
    const isNavItem = navMenuItems.some(item => item.path === pathname);

    const newActiveIdx =
      pathname.match(new RegExp(`^${urlKeyPath}[/]?$`)) ? 0 :
        navMenuItems.findIndex(x => {
          if (isNavItem) {
            return pathname === x.path;
          } else {
            return x.path === urlKeyPath + '/' ? false :
              pathname.match(new RegExp(`^${x.initialPath}`));
          }
        });
    setNavActiveIdx(newActiveIdx);
  }, [itin.localData.urlKey, navMenuItems, pathname, setNavActiveIdx, urlKeyPath]);

  // to clean ewaDocs when users switch trip or press refresh btn
  useEffect(() => {
    setEwaDocs([]);
  }, [itin?.id, setEwaDocs])

  if (!navMenuItems) return <LoadingScreen />

  // to re-use the old gallery with a new document structure for the displayMode gallery
  const buildDocGalleryRouting = () => {
    if (!itin?.documents?.length) return;
    const galleryDocIds = itin.documents?.filter(doc => doc.displayMode === "gallery").map(doc => doc.id);
    const listDocs = itin.documents?.filter(doc => doc.displayMode === "list");
    const menuSegueIds = itin.menu.map(item => item.segueId);

    const getPathIdsChain = (childId, pathIds) => {
      const parentDoc = listDocs.find(listDoc => listDoc.entries.includes(childId));
      if (!parentDoc) return pathIds;

      pathIds.unshift(parentDoc.id);

      if (menuSegueIds.includes(parentDoc.id)) {
        pathIds[0] = parentDoc.pathName;
        return pathIds;
      }

      return getPathIdsChain(parentDoc.id, pathIds);
    };

    const paths = galleryDocIds.map(galleryId => {
      if (menuSegueIds.includes(galleryId)) {
        const gallery = itin.documents?.find(doc => doc.id === galleryId);
        return `documents/${gallery.pathName}/gallery`;
      } else {
        const pathIdsChain = getPathIdsChain(galleryId, [galleryId]);
        return `documents/${pathIdsChain.join('/')}/gallery`;
      }
    });

    return (
      <>
        {paths.map((path, index) => (
          <Route key={index} path={path} element={<Brief type="gallery" isDocuments />}>
            <Route path=":slideNum" element={<DailySummary type="gallery" slideData={null} isDocuments />} />
          </Route>
        ))}
      </>
    );
  };

  return (
    <Routes>
      <Route path="/" element={itin.type === 'inspiration' ? <Navigate to={buildFullPath(menuItemOptions.inspirations, null)} /> : <Home />} />
      {
        itin.type !== 'inspiration' && (
          <>
            <Route path="summary" element={<Summary type="storyboard" />} />
            <Route path="daily" element={<Brief type="storyboard" />} >
              <Route path=":slideNum" element={<DailySummary type="storyboard" />} />
              <Route path=":slideNum/flights" element={<DailyFlights />} >
                <Route path=":id" element={<DailyFlights />} />
              </Route>
              <Route path=":slideNum/posts" element={<DailyPosts />}>
                <Route path=":id" element={<DailyPosts />} >
                  <Route path="edit" element={<DailyPosts />} />
                </Route>
              </Route>
              <Route path=":slideNum/maps" element={<DailyMap />}>
                <Route path=":poiId" element={<DailyMap />} />
                <Route path=":poiId/features" element={<DailyMap />} />
              </Route>
              <Route path=":slideNum/weather" element={<DailyWeather />} />
            </Route>
            <Route path="daily/:slideNum/view/:docId" element={<DailyDocumentViewer type="storyboardDocuments" />} />
            <Route path="posts" element={<Posts />} >
              <Route path="edit/:id" element={<Posts />} />
              <Route path="preview/:id" element={<Posts />} />
              <Route path=":slideNum" element={<Posts />} >
                <Route path="preview/:id" element={<Posts />} />
                <Route path="edit/:id" element={<Posts />} />
              </Route>
            </Route>
            <Route path="pois" element={<PoiList />} />
            <Route path="maps/:poiId/view/:docId" element={<DocumentViewer  type="mapDocuments"/>} />
            <Route path="maps" element={<Map />}>
              <Route path=":poiId" element={<Map />} />
              <Route path=":poiId/features" element={<Map />} />
            </Route>
            <Route path="weather" element={<Weather />} >
              <Route path=":idx" element={<Weather />} />
            </Route>
            <Route path="flights" element={<Flights />} >
              <Route path=":id" element={<Flights />} />
            </Route>
            <Route path="gallery/all" element={<Summary type="gallery" />} />
            <Route path="gallery" element={<Brief type="gallery" />} >
              <Route path=":slideNum" element={<DailySummary type="gallery" />} />
            </Route>
            {buildDocGalleryRouting()}
            <Route path="documents/*" element={<Documents />} />
            <Route path="directory/*" element={<Directory type="directories" />} />
            <Route path="vouchers/*" element={<Directory type="vouchers" />} />
            <Route path="daily-activities" element={<Directory type="dailyActivities" />}>
              <Route path=":slideNum/*" element={<Directory type="dailyActivities" />} />
            </Route>
            <Route path="hotel-info" element={<HotelInfo />} >
              <Route path="features" element={<HotelInfo /> } />
            </Route>
          </>
        )
      }
      <Route path="inspirations" element={<Inspirations />} >
        <Route path=":sectionNum" element={<Inspirations />} >
          <Route path=":slideNum" element={<Inspirations />} />
        </Route>
      </Route>
      {hamburgerMenuNavigationItems.map((item, index) => (
        <Route key={index} path={item.path} element={item.component} />
      ))}
      <Route path="*" element={<NotFoundScreen />} />
    </Routes>
  );
};

export default Itinerary;